'use client'

import { useEffect, useRef, useState } from 'react'
import { LiveScoreboard } from './LiveScoreboard'
import { useLiveSession, type LiveSessionData } from '@/hooks/useLiveSession'
import { useSiteTheme } from '@/hooks/useSiteTheme'
import { useSubstitutionScene } from '@/hooks/useSubstitutionScene'
import { useBlockScene } from '@/hooks/useBlockScene'
import { useAceScene } from '@/hooks/useAceScene'
import type { MatchState } from '@/lib/live/state'
import { SetFlowChart, SetFlowChartBars } from '@/components/portal/SetFlowChart'
import { buildLiveSetFlows, buildLiveMarkers } from '@/lib/live/set-flow'
import { buildLiveNarrative } from '@/lib/live/narrative'
import { LiveNarrative } from './LiveNarrative'

interface LiveEventRow {
  id: number
  revision: number
  type: string
  team: string | null
  payload: Record<string, unknown>
  created_at: string
}

/**
 * Viewer di una sessione live: stato real-time (WS → SSE → polling) +
 * cronaca + andamento dei set + indicatore di trasporto. Lo stato iniziale
 * arriva già dal server (prop `session`) così il primo paint è corretto.
 */
export function LiveSessionView({
  session,
  coordinates = null,
  accentColor = null,
  playerNames = null,
}: {
  session: LiveSessionData
  /**
   * Gara in programma: data, ora e palazzetto da mostrare **dentro** il
   * mini-tabellone al posto dei parziali. Il server decide *se* mostrarle
   * (fino a un'ora dal via) e passa `null` quando non servono.
   */
  coordinates?: { date: string; time: string; venue: string } | null
  /** Accento del **club di casa** (stesso nei due temi). */
  accentColor?: string | null
  /** Nomi per numero dalle distinte (piè della sostituzione). */
  playerNames?: { home: Record<string, string>; away: Record<string, string> } | null
}) {
  const { data, connected, transport } = useLiveSession(session.sessionKey, session)
  // La sostituzione **non cambia lo stato**: viaggia solo negli eventi. La scena
  // si ricava dallo storico che alimenta già la cronaca (nessun secondo poller) e
  // la regola è la stessa della card della lista: `useSubstitutionScene`.
  const [snapshotReady, setSnapshotReady] = useState(false)
  // Il tabellone segue il tema del **sito** scelto dal visitatore.
  const { theme } = useSiteTheme()
  // Storico **completo** degli eventi (paginato all'apertura, poi incrementale):
  // alimenta sia i grafici dell'andamento sia la cronaca, che così racconta tutta
  // la partita e non solo gli ultimi trenta eventi.
  const [allEvents, setAllEvents] = useState<LiveEventRow[]>([])

  // Storico completo: si carica **una volta** per sessione (paginando all'indietro
  // dal più recente), poi resta aggiornato in modo incrementale.
  const lastEventIdRef = useRef(0)
  // Distingue «storico non ancora arrivato» da «storico arrivato ma vuoto»: una
  // sessione appena creata non ha eventi, e con `lastId = 0` l'incrementale deve
  // comunque partire (chiede `after=0`, cioè tutto). Senza questa distinzione il
  // primo caricamento a vuoto bloccava per sempre gli aggiornamenti.
  const snapshotLoadedRef = useRef(false)
  useEffect(() => {
    let cancelled = false
    lastEventIdRef.current = 0
    snapshotLoadedRef.current = false
    // Cambio di sessione: il segnaposto degli eventi non si eredita.
    setSnapshotReady(false)
    setAllEvents([])
    const load = async () => {
      const collected: LiveEventRow[] = []
      let before: number | null = null
      for (let page = 0; page < 10 && !cancelled; page++) {
        const qs = new URLSearchParams({ limit: '100' })
        if (before !== null) qs.set('before', String(before))
        try {
          const r = await fetch(`/api/live/${session.sessionKey}/events?${qs.toString()}`, {
            cache: 'no-store',
          })
          if (!r.ok) break
          const j = (await r.json()) as { events: LiveEventRow[] }
          const batch = j.events ?? []
          if (batch.length === 0) break
          collected.push(...batch)
          before = Math.min(...batch.map((e) => e.id))
          if (batch.length < 100) break
        } catch {
          break
        }
      }
      if (!cancelled) {
        const sorted = collected.sort((a, b) => a.id - b.id)
        setAllEvents(sorted)
        lastEventIdRef.current = sorted[sorted.length - 1]?.id ?? 0
        snapshotLoadedRef.current = true
        setSnapshotReady(true)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [session.sessionKey])

  // A ogni punto (la revisione cambia) si chiedono **solo** gli eventi nuovi
  // (`after=<ultimo id>`): è così che il grafico del set in corso cresce punto per
  // punto senza ricaricare lo storico — una richiesta da poche righe, non dieci
  // pagine. Prima lo storico si ricaricava solo alla chiusura di un set, e il set
  // in corso restava fermo al punteggio del primo caricamento.
  useEffect(() => {
    if (!snapshotLoadedRef.current) return // lo storico iniziale non è ancora arrivato
    const after = lastEventIdRef.current
    let cancelled = false
    const load = async () => {
      try {
        const r = await fetch(
          `/api/live/${session.sessionKey}/events?after=${after}&limit=100`,
          { cache: 'no-store' },
        )
        if (!r.ok) return
        const j = (await r.json()) as { events: LiveEventRow[] }
        const batch = j.events ?? []
        if (cancelled || batch.length === 0) return
        setAllEvents((prev) => {
          const known = new Set(prev.map((e) => e.id))
          const merged = [...prev, ...batch.filter((e) => !known.has(e.id))].sort((a, b) => a.id - b.id)
          lastEventIdRef.current = merged[merged.length - 1]?.id ?? after
          return merged
        })
      } catch {
        /* il grafico resta sull'ultimo stato noto */
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [session.sessionKey, data.revision])

  // La scena di sostituzione, dalla regola condivisa con la card della lista.
  const substitution = useSubstitutionScene(allEvents, snapshotReady)
  // Il muro è un evento come la sostituzione: stessa sorgente, stessa regola.
  const block = useBlockScene(allEvents, snapshotReady)
  // L'ace batte nel campo avversario: stesso flusso di eventi, altra scena.
  const ace = useAceScene(allEvents, snapshotReady)

  // Lo stato che disegna il tabellone: i default della sessione (già normalizzati
  // dal server) sotto, l'ultimo aggiornamento live sopra. Se un payload arriva
  // **parziale** — o la sessione nasce con `state` vuoto — i campi mancanti
  // restano quelli di partenza invece di diventare `undefined` (che faceva
  // esplodere `ScorePanel` su `state.setHistory.length`).
  const state: MatchState = { ...session.state, ...data.state }

  // Gli stessi due disegni del match center (orizzontale da `md`, a barre sotto),
  // alimentati dal flusso ricostruito dagli eventi live.
  // Il live si legge dall'alto: il set in corso (o l'ultimo chiuso) sta in cima,
  // i precedenti sotto.
  const flows = buildLiveSetFlows(allEvents, state.setHistory ?? []).reverse()
  const markers = buildLiveMarkers(allEvents)
  // La cronaca: stesso storico, raccontato. Punteggio dopo ogni punto, chi l'ha
  // fatto, e le note di contesto (palla set annullata, set chiuso, allungo) che
  // dal solo tipo d'azione non si vedrebbero.
  const cronaca = buildLiveNarrative(allEvents, state.setHistory ?? [])

  const status = data.status
  const notStarted = status === 'scheduled' || status === 'warmup'
  const ended = status === 'finished' || status === 'cancelled' || status === 'postponed'
  // Tre esiti diversi, tre frasi: «Partita conclusa» su una gara rinviata o
  // annullata è falso. L'etichetta è la stessa del tabellone
  // (`match-presentation.ts`: «Gara rinviata» / «Gara annullata»).
  const endedText = status === 'cancelled'
    ? 'Gara annullata: non verrà giocata. Il calendario riporta la variazione ufficiale.'
    : status === 'postponed'
      ? 'Gara rinviata: la nuova data è indicata nel calendario. Il risultato ufficiale prevale su quello mostrato qui.'
      : 'Partita conclusa. Il risultato federale ufficiale prevale su quello mostrato qui.'

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="league-kicker">Live</p>
        <TransportBadge transport={transport} connected={connected} />
      </div>

      <LiveScoreboard
        state={state}
        status={status}
        substitution={substitution}
        coordinates={coordinates}
        tone={theme}
        accentColor={accentColor ?? undefined}
        playerNames={playerNames}
        block={block}
        ace={ace}
      />

      {flows.length > 0 && (
        <div className="space-y-4">
          {flows.map((flow) => (
            <div
              key={flow.set}
              className="rounded-2xl border border-[color:var(--league-line)] bg-[color:var(--league-surface)] p-4"
              data-grafico-live-set={flow.set}
            >
              <p className="mb-1 text-[11px] uppercase tracking-wide text-[color:var(--league-muted)]">
                Andamento {flow.set}° set
              </p>
              <div className="hidden md:block" data-grafico-set="h">
                <SetFlowChart
                  flow={flow}
                  home={session.homeTeam}
                  away={session.awayTeam}
                  idPrefix={`live-setflow-${flow.set}`}
                  markers={markers
                    .filter((m) => m.set === flow.set)
                    .map((m) => ({
                      total: m.total,
                      side: m.side,
                      label: m.label,
                      title: m.title,
                      labelSide: m.labelSide,
                    }))}
                />
              </div>
              <div className="md:hidden" data-grafico-set="bars">
                <SetFlowChartBars
                  flow={flow}
                  home={session.homeTeam}
                  away={session.awayTeam}
                  markers={markers
                    .filter((m) => m.set === flow.set)
                    .map((m) => ({
                      total: m.total,
                      side: m.side,
                      label: m.label,
                      title: m.title,
                      labelSide: m.labelSide,
                    }))}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {notStarted && (
        <p className="rounded-xl border border-dashed border-[color:var(--league-line)] px-4 py-5 text-center text-[14px] text-[color:var(--league-muted)]">
          La partita non è ancora iniziata. I dati aggiornati compariranno qui in tempo reale.
        </p>
      )}

      {ended && (
        <p className="rounded-xl border border-dashed border-[color:var(--league-line)] px-4 py-5 text-center text-[14px] text-[color:var(--league-muted)]">
          {endedText}
        </p>
      )}

      <LiveNarrative sets={cronaca} homeTeam={session.homeTeam} awayTeam={session.awayTeam} />
    </div>
  )
}

function TransportBadge({ transport, connected }: { transport: string; connected: boolean }) {
  if (transport === 'ws' && connected) {
    return (
      <span className="league-badge bg-[color:var(--league-surface-alt)] text-[color:var(--league-muted)]">
        <span className="mr-1 inline-block h-2 w-2 rounded-full bg-green-500" aria-hidden />
        In diretta
      </span>
    )
  }
  if (transport === 'ws' && !connected) {
    return (
      <span className="league-badge bg-[color:var(--league-surface-alt)] text-[color:var(--league-muted)]">
        <span className="mr-1 inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500" aria-hidden />
        Riconnessione…
      </span>
    )
  }
  if (transport === 'sse') {
    return (
      <span className="league-badge bg-[color:var(--league-surface-alt)] text-[color:var(--league-muted)]">
        Aggiornamento in corso
      </span>
    )
  }
  return (
    <span className="league-badge bg-[color:var(--league-surface-alt)] text-[color:var(--league-muted)]">
      Aggiornamento lento
    </span>
  )
}
